import type {
  OrderGetResponseType,
  OrderListResponseType,
  OrderV2Dto,
} from "@sellar-io/api-v2/types";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useFirebaseUser } from "../../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../../apiV2Request";
import { ServerStateKeys } from "../../ServerStateKeys";

export const useOrderCancel = () => {
  const { business } = useSellarCurrentBusiness();
  const queryClient = useQueryClient();
  const { user } = useFirebaseUser();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: OrderV2Dto.OrderCancelRequestType;
    }) =>
      apiV2Request({
        user,
        business,
        method: "post",
        path: `orders/v2/${id}/cancel`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse(response)
            .data as OrderV2Dto.OrderCancelResponseType,
      ),
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.Orders, id],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.Orders],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.OrdersLegacy],
      });
      queryClient.invalidateQueries({
        queryKey: [business?.id, ServerStateKeys.OrderComments],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.OrderEvents, id],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.LegalEntity],
      });
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [business?.id, ServerStateKeys.OrderIntegrationValue, id],
        });
      }, 1000);

      queryClient.setQueriesData<OrderGetResponseType>(
        {
          predicate: ({ queryKey }) => {
            return (
              queryKey[0] === user?.uid &&
              queryKey[1] === business?.id &&
              queryKey[2] === ServerStateKeys.Orders &&
              queryKey[3] === id
            );
          },
        },
        (data) => {
          if (!data) return data;
          return {
            ...data,
            cancelledAt: response.cancelledAt,
          };
        },
      );

      queryClient.setQueriesData<InfiniteData<OrderListResponseType>>(
        {
          predicate: ({ queryKey }) => {
            return (
              queryKey[0] === user?.uid &&
              queryKey[1] === business?.id &&
              queryKey[2] === ServerStateKeys.Orders &&
              typeof queryKey[3] === "object"
            );
          },
        },
        (data) => {
          if (!data?.pages) return data;
          return {
            ...data,
            pages: data.pages.map((page) =>
              page.map((order) =>
                order.id === response.id ? response : order,
              ),
            ),
          };
        },
      );
    },
  });
};
